/**
 * formatterCurrencyDirective
 *
 * @author Dec Norton <decnorton@gmail.com>
 */
import StringMask from 'string-mask';

formatterCurrencyDirective.$inject = ['$locale'];

export function formatterCurrencyDirective($locale) {
    return {
        priority: 100,
        require: 'ngModel',
        restrict: 'A',
        link: function(scope, element, attrs, ngModel) {
            const decimalDelimiter = $locale.NUMBER_FORMATS.DECIMAL_SEP;
            const thousandsDelimiter = $locale.NUMBER_FORMATS.GROUP_SEP;
            const symbol = $locale.NUMBER_FORMATS.CURRENCY_SYM;
            const symbolSeparation = ' ';
            const decimals = 2;

            function maskFactory(decimals) {
                const decimalsPattern = decimals > 0 ? decimalDelimiter + new Array(decimals + 1).join('0') : '';

                const maskPattern = `${symbolSeparation}#${thousandsDelimiter}##0${decimalsPattern}`;

                return new StringMask(maskPattern, {
                    reverse: true,
                });
            }

            const moneyMask = maskFactory(decimals);

            function formatter(value) {
                if (ngModel.$isEmpty(value)) {
                    return value;
                }

                value = value / 100;

                const prefix = value < 0 ? '-' : '';

                const valueToFormat = prepareNumberToFormatter(value, decimals);

                return prefix + symbol + moneyMask.apply(valueToFormat);
            }

            function parser(value) {
                if (ngModel.$isEmpty(value)) {
                    return null;
                }

                let actualNumber = value.replace(/[^\d]+/g, '');
                actualNumber = actualNumber.replace(/^[0]+([1-9])/, '$1');
                actualNumber = actualNumber || '0';

                let formattedValue = symbol + moneyMask.apply(actualNumber);

                const isNegative = value[0] === '-';
                const shouldInvertSign = value.slice(-1) === '-';

                // Only apply the minus sign if it is negative or (exclusive)
                // needs to be negative and the number is different from zero
                if (shouldInvertSign !== isNegative && !!actualNumber) {
                    formattedValue = '-' + formattedValue;
                }

                if (value !== formattedValue) {
                    ngModel.$setViewValue(formattedValue);
                    ngModel.$render();
                }

                return formattedValue ? parseInt(formattedValue.replace(/[^\d\\-]+/g, '')) : null;
            }

            ngModel.$formatters.push(formatter);
            ngModel.$parsers.push(parser);

            if (attrs.min) {
                let minVal;

                ngModel.$validators.min = function(modelValue, viewValue) {
                    if (ngModel.$isEmpty(viewValue)) {
                        // consider empty models to be valid
                        return true;
                    }

                    return modelValue >= minVal;
                };

                scope.$watch(attrs.min, function(value) {
                    minVal = value;
                    ngModel.$validate();
                });
            }

            if (attrs.max) {
                let maxVal;

                ngModel.$validators.max = function(modelValue, viewValue) {
                    if (ngModel.$isEmpty(viewValue)) {
                        // consider empty models to be valid
                        return true;
                    }

                    return modelValue <= maxVal;
                };

                scope.$watch(attrs.max, function(value) {
                    maxVal = value;
                    ngModel.$validate();
                });
            }
        },
    };

    function clearDelimitersAndLeadingZeros(value) {
        if (value === '0') {
            return '0';
        }

        return value
            .replace(/^-/, '')
            .replace(/^0*/, '')
            .replace(/[^0-9]/g, '');
    }

    function prepareNumberToFormatter(value, decimals) {
        return clearDelimitersAndLeadingZeros(parseFloat(value).toFixed(decimals));
    }
}
